import { useState, useEffect, useRef } from 'react';
import type { UserWidgetPreferenceBase } from '../types';

interface UseOptimisticLayoutProps<TPreference extends UserWidgetPreferenceBase> {
  initialLayout: TPreference[];
  saveLayout: (layout: TPreference[]) => Promise<void>;
  debounceMs?: number;
  /** Clave de localStorage para persistir el layout como respaldo local */
  storageKey?: string;
}

/** Lee el layout desde localStorage. Devuelve null si no hay nada o el JSON está corrupto. */
function readFromStorage<T>(key: string): T[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as T[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Hook genérico responsable de mantener el estado local del Grid y sincronizarlo
 * con un callback de guardado (saveLayout) aplicando un debounce estricto.
 * Si se provee `storageKey`, persiste el layout en localStorage como respaldo ante
 * cierres de pestaña antes de que complete el debounce.
 */
export const useOptimisticLayout = <TPreference extends UserWidgetPreferenceBase>({
  initialLayout,
  saveLayout,
  debounceMs = 800,
  storageKey
}: UseOptimisticLayoutProps<TPreference>) => {
  const [layout, setLayout] = useState<TPreference[]>(() => {
    if (storageKey) {
      const saved = readFromStorage<TPreference>(storageKey);
      if (saved) return saved;
    }
    return initialLayout;
  });
  const [isSaving, setIsSaving] = useState(false);

  // Sincronizar layout local cuando initialLayout cambia (ej: cambio de usuario/roles)
  const prevInitialRef = useRef(initialLayout);
  useEffect(() => {
    if (JSON.stringify(prevInitialRef.current) !== JSON.stringify(initialLayout)) {
      prevInitialRef.current = initialLayout;
      setLayout(initialLayout);
    }
  }, [initialLayout]);

  // useRef para mantener una copia fresca del layout sin disparar re-renders en el efecto
  const layoutToSaveRef = useRef<TPreference[]>(layout);
  // Flag para omitir el guardado en el montaje inicial
  const isFirstRender = useRef(true);

  useEffect(() => {
    layoutToSaveRef.current = layout;
  }, [layout]);

  // Persistencia en localStorage ante cada cambio (respaldo local inmediato)
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch {
      // Ignorar errores de cuota
    }
  }, [layout, storageKey]);

  // Debounce → REST (omite el primer render para no guardar en montaje)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      setIsSaving(true);
      try {
        await saveLayout(layoutToSaveRef.current);
      } catch (error) {
        console.error('Error auto-guardando layout:', error);
      } finally {
        setIsSaving(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [layout, debounceMs, saveLayout]);

  /**
   * Actualiza el estado local inmediatamente (Optimistic UI)
   */
  const updateLayout = (newLayout: TPreference[]) => {
    setLayout(newLayout);
  };

  return {
    layout,
    updateLayout,
    isSaving
  };
};
